import styled, { theme } from '@nobia/zeus-components/lib/styled'
import rem from '@nobia/zeus-components/lib/styled/rem'
import settings from '@nobia/zeus-components/lib/settings'
import { Icon as ZeusIcon } from '@nobia/zeus-components/lib/icons'
import React, { useState, useEffect } from 'react'
import { useQuery } from '@apollo/client'

import { margins } from '../../styles/margins'
import { useTranslation } from '../../utils/useTranslation'
import { Icon as IconUi } from '../icons'
import { Heading2 } from '../typography'
import { useSettings } from '../user-settings/SettingsProvider'
import { StickyHeader } from '.'
import { StickyHeaderDefaultProps, StickyHeaderPropTypes } from './StickyHeader'
import { MyProjectConfigQuery } from '../../graphql/MyProjectConfigQuery'

const Header = styled.header`
  background-color: ${theme('header.wrapper.background')};
  color: ${theme('header.wrapper.color')};
  border: ${theme('header.wrapper.border')};
  display: flex;
  align-items: center;
  height: ${theme('header.wrapper.height')};

  ${(p) => p.theme.media.min.md`
    height: ${theme('header.wrapper.md.height')};
  `}
  border-bottom: ${theme('navigation.wrapper.border')};
`

const LeftContent = styled.div`
  display: flex;
  flex: 1;
  align-content: center;
  justify-content: flex-start;
`

const CenterContent = styled.div`
  display: none;

  ${(p) => p.theme.media.min.md`
    display: flex;
    flex: 1;
    align-content: center;
    justify-content: center;
  `}
`

const RightContent = styled.div`
  display: flex;
  flex: 1;
  align-content: center;
  justify-content: flex-end;

  ${(p) =>
    p.isMagnetBrand &&
    `
    position: relative;
    right: 22px;
  `}
`

const ButtonLink = styled.a`
  border: none;
  color: inherit;
  background-color: inherit;
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  ${(p) => p.theme.media.min.md`
    margin-bottom: 0;
  `}
  :hover {
    color: inherit;
  }
`
const HeaderItem = styled(ButtonLink)`
  padding: ${theme('header.logo.margin')};
`

const IconLink = styled.div`
  padding: ${rem(margins.s)};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  ${(p) =>
    p.isMagnetBrand &&
    `
    background-color: ${p.theme.colors.gold};
    border-radius: 50%;
    padding: 7px;
  `}
`

const Icon = styled(IconUi)`
  display: block;
`

const Logo = styled(Icon).attrs({ name: 'logo' })`
  width: ${theme('header.logo.width')};
  margin-left: ${theme('header.logo.marginLeft')};
  ${(p) => p.theme.media.min.md`
    width: ${theme('header.logo.md.width')};
  `}
`

const Title = styled(Heading2)`
  font-weight: ${theme('header.title.fontWeight')};
  font-size: ${(p) => rem(p.theme.header.title.fontSize)};
  line-height: ${(p) => rem(p.theme.header.title.lineHeight)};
  color: inherit;
  margin: 0;
`

const WarningMessage = styled.div`
  background-color: ${(p) => p.theme.colors.tone1};
  padding-block: ${rem(10)};
  padding-inline: ${rem(10)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(10)};
  width: 100%;

  ${(p) => p.theme.media.min.md`
    padding-inline: ${rem(20)};
  `}
`

const WarningText = styled.div`
  font-size: ${rem(12)};
  flex: 1;
  text-align: left;
  display: flex;

  ${(p) => p.theme.media.min.md`
    font-size: ${rem(13)};
    text-align: center;
    justify-content: center;
  `}
`

const InfoIcon = styled(ZeusIcon)`
  padding-right: ${rem(10)};
  position: relative;
  top: ${rem(2)};
`

const CloseButton = styled(IconUi)`
  width: ${rem(10)};
  height: ${rem(10)};
  cursor: pointer;
  flex-shrink: 0;
`

const WarningContent = styled.span`
  display: block;
`

const BrandHeader = ({ fixed, fixedOffset }) => {
  const { openSettings } = useSettings()
  const { t } = useTranslation('app.components.header')
  const isMagnetBrand = settings.brand.includes('magnet')
  
  const { data: configData } = useQuery(MyProjectConfigQuery)
  const [isDismissed, setIsDismissed] = useState(false)
  const headerRef = React.useRef(null)

  useEffect(() => {
    const dismissed = sessionStorage.getItem('warningMessageDismissed')
    if (dismissed === 'true') {
      setIsDismissed(true)
    }
  }, [])

  const handleDismiss = () => {
    sessionStorage.setItem('warningMessageDismissed', 'true')
    setIsDismissed(true)
  }

  const showWarning = 
    configData?.config?.notification?.enabled && 
    !isDismissed &&
    configData?.config?.notification?.text

  useEffect(() => {
    if (headerRef.current) {
      const updateHeaderHeight = () => {
        const height = headerRef.current.offsetHeight
        document.documentElement.style.setProperty('--header-total-height', `${height}px`)
      }
      
      updateHeaderHeight()
      
      const resizeObserver = new ResizeObserver(updateHeaderHeight)
      resizeObserver.observe(headerRef.current)
      
      return () => {
        resizeObserver.disconnect()
      }
    }
  }, [showWarning])

  return (
    <StickyHeader fixed={fixed} fixedOffset={fixedOffset}>
      <div ref={headerRef}>
        {showWarning && (
          <WarningMessage>
            <WarningText>
              <InfoIcon type="info-inverse" width={rem(18)} height={rem(18)} />
              <WarningContent>{configData.config.notification.text}</WarningContent>
            </WarningText>
            <CloseButton name="close" onClick={handleDismiss} />
          </WarningMessage>
        )}
        <Header>
        <LeftContent>
          <HeaderItem href="/" target="_blank">
            <Logo />
          </HeaderItem>
        </LeftContent>
        <CenterContent>
          <Title>{t('title')}</Title>
        </CenterContent>
        <RightContent isMagnetBrand={isMagnetBrand}>
          <IconLink isMagnetBrand={isMagnetBrand} onClick={openSettings}>
            {isMagnetBrand ? (
              <Icon name="user" width="22px" height="22px" />
            ) : (
              <Icon name="settings" width="21px" height="21px" />
            )}
          </IconLink>
        </RightContent>
      </Header>
      </div>
    </StickyHeader>
  )
}

// eslint-disable-next-line no-unused-vars
const { children, ...stickyHeaderPropTypes } = StickyHeaderPropTypes

BrandHeader.defaultProps = {
  ...StickyHeaderDefaultProps,
}

BrandHeader.propTypes = {
  ...stickyHeaderPropTypes,
}

export default BrandHeader
